<template>
    <ul
        v-if="filterTags.length"
        class="l-ss-c-filter-tags">
        <li
            v-for="tag in filterTags"
            :key="`filter-tag-${tag.id}`"
            class="l-ss-c-filter-tags-item"
            @click="onFilterTagClick(tag)">
            <span v-html="tag.value"></span>
            <i
                v-html="iconCloseCircle"
                class="l-ss-c-icon"></i>
        </li>
        <li
            v-if="isShowResetButton"
            class="l-ss-c-filter-tags-reset-button"
            @click="$emit('reset-filters')">
            <span>{{ $t('filters.resetAllText') }}</span>
        </li>
    </ul>
</template>

<script>
    import iconCloseCircle from '@/core/assets/images/icons/close-circle.svg';

    import firstLetterToUpperCase from '@/core/helpers/first-letter-to-upper-case';

    import useFiltersCategoriesRoot
        from '@/core/components/common/filters/filter-types/filter-tags/use-filters-categories-root';

    export default {
        props: {
            activeFilters: {
                type: Array,
                default: () => ([]),
            },
            filters: {
                type: Array,
                default: () => ([]),
            },
            symbol: {
                type: String,
                default: '',
            },
            showResetBtn: {
                type: Boolean,
                default: true,
            },
            allCategoriesTree: {
                type: Array,
            },
        },
        computed: {
            iconCloseCircle() {
                return iconCloseCircle;
            },
            isShowResetButton() {
                return this.showResetBtn && Boolean(this.filterTags?.length > 1);
            },
            categoriesNameMap() {
                let map = {};
                const categoryFilter = this.filters.find(item => item.id === 'category');
                const categoryFilterOptions = categoryFilter?.options || [];

                map = categoryFilterOptions.reduce((accumulator, currentOption) => {
                    accumulator[currentOption.id] = currentOption.name;
                    return accumulator;
                }, {});

                return map;
            },
            categoriesAsTree() {
                return this.$configuration.categoriesAsTree;
            },
            filterTags() {
                const tags = [];

                if (this.activeFilters?.length) {
                    this.activeFilters.forEach((filterItem, filterIndex) => {
                        const createTag = (options) => {
                            return {
                                filterIndex,
                                field: filterItem.field,
                                condition: filterItem.condition,
                                id: crypto.randomUUID(),
                                ...options,
                            };
                        };

                        const prefix = !this.$screen.isMobile ?
                            `${this.$t(`filters.byType.${filterItem.field}`).label || firstLetterToUpperCase(filterItem.field)}: ` :
                            '';

                        if (filterItem.options) {
                            const isTreeCategory = filterItem.field === 'category' && this.categoriesAsTree;

                            const options = isTreeCategory ?
                                useFiltersCategoriesRoot(this.allCategoriesTree, filterItem.options) :
                                filterItem.options;

                            options.forEach((optionItem, optionIndex) => {
                                let newTag = null;

                                if (isTreeCategory) {
                                    newTag = createTag({
                                        optionIndex,
                                        childCategoryIds: optionItem.childCategoryIds,
                                        rootCategoryId: optionItem.id,
                                        value: optionItem.title,
                                    });
                                } else {
                                    newTag = createTag({
                                        optionIndex,
                                        value: filterItem.field === 'category' ? this.getTagValue(optionItem) : optionItem,
                                    });
                                }

                                if (newTag.value === 'true' || newTag.value === 'false') {
                                    newTag.value = this.$t(`filters.${newTag.value}`);
                                }

                                if (newTag.value) {
                                    newTag.value = prefix + newTag.value;

                                    tags.push(newTag);
                                }
                            });
                        } else if (filterItem.condition === 'range') {
                            let unit = this.$configuration.filterConfig[filterItem.field]?.unit || this.symbol;

                            unit = (filterItem.field === 'price' && unit) ?
                                ` ${unit}` :
                                '';

                            const defaultTag = {
                                min: filterItem.min,
                                max: filterItem.max,
                            };

                            if (filterItem.min) {
                                const fromTag = createTag({
                                    ...defaultTag,
                                    value: prefix + `${this.$t('filters.from')} ${(+filterItem.min).toLocaleString()}` + unit,
                                    option: 'min',
                                });

                                tags.push(fromTag);
                            }

                            if (filterItem.max) {
                                const toTag = createTag({
                                    ...defaultTag,
                                    value: prefix + `${this.$t('filters.to')} ${(+filterItem.max).toLocaleString()}` + unit,
                                    option: 'max',
                                });

                                tags.push(toTag);
                            }
                        } else if (filterItem.field === '_is_available') {
                            if (filterItem.value) {
                                const tag = createTag({
                                    value: this.$t('filters.byType._is_available').checked,
                                });

                                tags.push(tag);
                            }
                        }
                    });
                }

                return tags;
            },
        },
        methods: {
            getTagValue(value) {
                if (this.categoriesNameMap[value]) {
                    return this.categoriesNameMap[value];
                }

                return '';
            },
            onFilterTagClick(tag) {
                const params = {
                    field: tag.field,
                };

                if (tag.optionIndex !== undefined && tag.filterIndex !== undefined) {
                    if (this.activeFilters[tag.filterIndex].options.length > 1) {
                        if (tag.field === 'category' && this.categoriesAsTree) {
                            const options = this.activeFilters[tag.filterIndex].options
                                .filter((option) => tag.rootCategoryId !== option && !tag.childCategoryIds.includes(option));

                            if (options.length) {
                                params.options = options;
                            }
                        } else {
                            params.options = this.activeFilters[tag.filterIndex].options.toSpliced(tag.optionIndex, 1);
                        }

                        params.condition = tag.condition;
                    }
                }

                if (tag.condition === 'range') {
                    if (tag.min) {
                        params.min = tag.min;
                    }

                    if (tag.max) {
                        params.max = tag.max;
                    }

                    delete params[tag.option];

                    if (params.min || params.max) {
                        params.condition = 'range';
                    }
                }

                this.$emit('change', params);
            },
        },
    };
</script>
